/**
 * 搜索列表 Search And Filter
 * 
 * @Author: Focci
 * @Date: 2023-07-20 15:37:23
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-20 15:37:23
 */

'use client'

import PropTypes from 'prop-types'
import SearchSuggestion from '@page/common/filter/combine/suggestion'
import useSearchRecording from '@hook/useSearchRecording'
import useTracking from '@hook/useTracking'
import dynamic from 'next/dynamic'
import useProgressRouter from '@hook/useProgressRouter'
import useLazyComponent from '@hook/useLazyComponent'
import { Waypoint } from 'react-waypoint'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { urls, getApiSearchParam, config, modeType, args } from '@lib/parse'
import { useLocale, useTranslations } from 'next-intl'
import { forEach, isArray, isFunction } from 'lodash-es'
import { parseURLSearchParams, buildURLSearchParams } from '@lib/utils'
import { useSearchParams } from 'next/navigation'
import { handleSearchItemClick } from '@page/common/filter/combine'
import { getGroupedData } from '../combine/filter-modal/lib'
import { getSearchDefaultValue } from '../combine/lib'
import ResposiveAutocomplete from '../combine/resposive'
import { FilterSkeletonCircular, FilterSkeleton } from './FilterSkeleton'

const FilterModal = dynamic(() => import('../combine/filter-modal/index'), {
  ssr: false,
  loading: FilterSkeletonCircular
})

export default function ListingFilterBody({
  isMapListing,
  type = '',
  categoryName = '',
  inputClassName = '',
  filterClassName = 'flex-auto',
  filterStyle = {},
  displayFilterBar = true,
  statistic = null,
  searchParam = {},
  apiParam = {},
  options = [],
  onChange,
  handleItem,
  autocompleteTriggerProps
}) {
  const locale = useLocale()
  const autocompleteRef = useRef()
  const router = useProgressRouter()
  const usp = useSearchParams()
  const ts = useTranslations('search')

  const { Wedget: FilterBar, getWedget: getFilterBar, ready } = useLazyComponent()
  const { addSearchRecord } = useSearchRecording()
  const { pushTrack, pushTrackSearch } = useTracking()
  
  const [open, setOpen] = useState(false)
  const [positionProps, setPositionProps] = useState()
  const [defaultSearchValue, setDefaultSearchValue] = useState([])

  const emitChange = useCallback((typ, pathname, data, actionType) => {
    if(isFunction(onChange)) {
      onChange({ type: typ, pathname, param: data, actionType })
      autocompleteRef.current?.handleClose?.()
    } else {
      router.routerReplace(pathname)
    }
  }, [onChange, router])

  // 点击完成按钮
  const handleSearchButtonClick = useCallback((v, extra) => {
    const val = v || defaultSearchValue
    
    if(val?.[0]?.type === 'rect') {
      return 
    }
    
    const groupedData = getGroupedData(val)
    const data = { ...groupedData, surrounding: !!extra?.surrounding, page: 1 }

    // 房源类型
    forEach(modeType, (k) => isArray(searchParam[k]) && (data[k] = []))
    const url = urls.listing(type, data, locale, isMapListing)
    
    // tracking
    const query = getApiSearchParam(groupedData, config.houseSearchRules)
    // house_search事件
    pushTrackSearch({ ...data, type }, query, isMapListing)
    
    // 添加搜索记录
    addSearchRecord({ type, searchParam: data })
    emitChange(type, url, data, 'input')
  }, [
    addSearchRecord, defaultSearchValue, emitChange, 
    locale, isMapListing, pushTrackSearch, searchParam, type
  ])
  
  // Filter modal 点击确定按钮
  const handleFilterConfirm = useCallback(({ filterObject, currentType }) => {
    let data = {}
    let url = ''
    
    // 如果是搜索的地图区域，则要处理url中的query
    if(filterObject.rect) {
      let query = usp.toString()

      // 如果搜索房源类型改变了
      if(searchParam.type !== currentType) {
        const qs = parseURLSearchParams(query)
        query = buildURLSearchParams(qs)
      }
      
      data = { ...searchParam, ...filterObject, page: 1 }
      url = `${urls.listing(currentType, data, locale, isMapListing)}${query ? `?${query}` : ''}`
    } else {
      data = { ...filterObject, page: 1 }
      url = urls.listing(currentType, data, locale, isMapListing)
    }
    
    // 添加搜索记录
    addSearchRecord({ type: currentType, searchParam: data })

    // house_search事件
    const query = args.listingApi(currentType, data)
    pushTrackSearch({ ...data, type: currentType }, query, isMapListing)

    emitChange(currentType, url, data, 'filter')
    setOpen(false)
  }, [addSearchRecord, pushTrackSearch, emitChange, usp, searchParam, locale, isMapListing])

  const handleItemCallback = useCallback((r) => {
    isFunction(handleItem) && handleItem(r)
    autocompleteRef.current?.handleClose?.()
  }, [handleItem])

  /**
   * 搜索结果点击学校, 房源详情，中介等
   */
  const handleChange = useCallback((_event, data, reason, opt) => {
    const p = { 
      data, reason, type, setter: setDefaultSearchValue, pushTrack, pushTrackSearch, locale 
    }
    const r = handleSearchItemClick(p, (v) => opt?.isZoneSchoolPicker && handleSearchButtonClick(v))
    
    if(r?.isToListings && isFunction(handleItem)) {
      handleItemCallback(r)
    } else {
      r?.url && router.routerPush(r.url)
    }
    
    // 添加搜索记录
    r?.targetData && addSearchRecord(r.targetData)
  }, [
    addSearchRecord, pushTrack, pushTrackSearch, router, handleItem, handleItemCallback,
    type, locale, handleSearchButtonClick
  ])

  const initial = useMemo(
    () => ({ 
      options, defaultSearchValue, positionProps, type, statistic, apiParam, searchParam 
    }), 
    [apiParam, searchParam, defaultSearchValue, positionProps, options, statistic, type]
  )

  const handleFilterClick = useCallback((d) => {
    setPositionProps(d?.props)
    setOpen(true)
  }, [])

  // 加载 filterbar
  const handleEnter = useCallback(() => getFilterBar(() => import('./filter-bar')), [getFilterBar])

  const searchSuggestionProps = useMemo(() => {
    const tmp = {
      isMapListing,
      inputValue: defaultSearchValue,
      inputValueSetter: setDefaultSearchValue,
      type,
      searchParam,
    }

    if(isFunction(handleItem)) {
      tmp.handleItem = handleItemCallback
    }

    return tmp
  }, [defaultSearchValue, handleItem, handleItemCallback, isMapListing, searchParam, type])

  useEffect(() => {
    const tmp = apiParam.rect
      ? [{ label: ts('maparea'), type: 'rect', value: apiParam.rect }]
      : getSearchDefaultValue(searchParam)

    setDefaultSearchValue(tmp)
  }, [apiParam.rect, searchParam, ts])

  return (
    <>
      <ResposiveAutocomplete
        ref={autocompleteRef}
        showSurrounding
        isMapListing={isMapListing}
        wrapperClassName={inputClassName}
        type={type}
        value={defaultSearchValue}
        placeholder={ts('searchInputPlaceholder')}
        onChange={handleChange}
        onSearchButtonClick={(_v, extra) => handleSearchButtonClick(_v, extra)}
        autocompleteTriggerProps={autocompleteTriggerProps}
        autocompleteProps={{
          noOptionsText: (
            <SearchSuggestion
              onChange={() => autocompleteRef.current.setChanged(true)}
              {...searchSuggestionProps}
            />
          )
        }}
      />
      {displayFilterBar && (
        <>
          <Waypoint onEnter={handleEnter}>
            <div className={filterClassName} style={filterStyle}>
              {!ready && <FilterSkeleton />}
              {ready && (
                <FilterBar
                  isMapListing={isMapListing}
                  categoryName={categoryName}
                  options={options}
                  type={searchParam.type}
                  searchParam={searchParam}
                  apiParam={apiParam}
                  disabled={open}
                  onClick={handleFilterClick}
                />
              )}
            </div>
          </Waypoint>
          {open && (
            <FilterModal
              open={open}
              initial={initial}
              onClose={() => setOpen(false)}
              onConfirm={handleFilterConfirm}
            />
          )}
        </>
      )}
    </>
  )
}

ListingFilterBody.propTypes = {
  type: PropTypes.string,
  categoryName: PropTypes.string,
  inputClassName: PropTypes.string,
  filterClassName: PropTypes.string,
  filterStyle: PropTypes.object,
  statistic: PropTypes.object,
  searchParam: PropTypes.object,
  apiParam: PropTypes.object,
  options: PropTypes.array,
  onChange: PropTypes.func,
}
